import { GridColDef, GridFilterInputSingleSelect, getGridStringOperators } from "@mui/x-data-grid-pro";
import { DAILY_TRANSACTION_AMOUNT_COLUMN, DAILY_TRANSACTION_SERVICE_FEE_COLUMN, isAdjustment } from "../../common";
import { CurbsuiteTransactionLogEntry } from "common/graphql/queries/GetCurbsuiteTransactions";
import { buildOperatorColumn, buildTransactionDateColumn, TRANSACTION_INDEX_COLUMN } from "./sharedColumns";
import { ADJUSTMENT_TYPE_LABEL } from "../../common/adjustments";
import { WALLET_TRANSACTION_TYPE_LABELS } from "./walletTransactionTypeLabels";
import {
  buildActionsColumnWithAdjustment,
  buildWalletActionsColumn,
  renderTypeCellWithAdjustment,
} from "./adjustmentColumns";

/**
 * Columns for the "wallet" variant: regular payees, bus-station-wallet, and airport-wallet (GSP's
 * "Monthly Charges" reshape, which swaps the Transaction Type column for Operator since every row
 * there is the same permit charge).
 */
export function buildWalletColumns({
  isAirportWallet,
  showsOperatorBilling,
  showsServiceFee,
  searchParams,
}: {
  isAirportWallet: boolean;
  showsOperatorBilling: boolean;
  /** Includes the Service Fee column (AUT-6175: `display_service_fee_column` flag). */
  showsServiceFee: boolean;
  /** The page's current search params, which each adjustment row's reference link extends. */
  searchParams: URLSearchParams;
}): GridColDef[] {
  return [
    TRANSACTION_INDEX_COLUMN,
    buildTransactionDateColumn({ isAirportWallet, isBusStationPark: false, showsOperatorBilling }),
    isAirportWallet
      ? buildOperatorColumn({ width: 220 })
      : ({
          field: "transactionType",
          headerName: "Transaction Type",
          type: "singleSelect",
          flex: 1,
          valueOptions: [...Object.values(WALLET_TRANSACTION_TYPE_LABELS), ADJUSTMENT_TYPE_LABEL],
          valueGetter({ row }) {
            // Adjustments have no gateway transaction of their own (AUT-6163). The cell value stays
            // the bare label so the singleSelect "equals" filter matches it; renderCell adds the
            // reason and the "Adjusts #" link under it.
            if (isAdjustment(row)) {
              return ADJUSTMENT_TYPE_LABEL;
            }
            if (row.transaction.gatewayTransactions.length > 0) {
              return (
                WALLET_TRANSACTION_TYPE_LABELS[row.transaction.gatewayTransactions[0].gatewayTransactionType] ||
                "Unknown"
              );
            }
            return "";
          },
          renderCell: renderTypeCellWithAdjustment(searchParams),
          filterOperators: getGridStringOperators()
            .filter((operator) => operator.value === "equals")
            .map((operator) => ({
              ...operator,
              InputComponent: operator.InputComponent ? GridFilterInputSingleSelect : undefined,
            })),
          filterable: true,
        } as GridColDef<CurbsuiteTransactionLogEntry, string>),
    DAILY_TRANSACTION_AMOUNT_COLUMN,
    ...(showsServiceFee ? [DAILY_TRANSACTION_SERVICE_FEE_COLUMN] : []),
    // Monthly Charges has no Type column (Operator takes its place), so an adjustment row's two-line
    // Type cell goes in the actions column there; elsewhere it's in Transaction Type, and the
    // actions column carries only the adjustment's chevron (AUT-6163).
    isAirportWallet
      ? buildActionsColumnWithAdjustment({ searchParams, getActions: () => [] })
      : buildWalletActionsColumn({ searchParams }),
  ];
}
